/**
 * Complete course sequence lookup table based on course-diagrams.md
 * This replaces the complex extension pattern system with direct lookups
 * for all valid course configurations and beat combinations.
 */

import { COURSE_TYPES, MARKS, FINISH_OPTIONS } from './constants.js';

/**
 * Complete lookup table structure:
 * courseType -> finishType -> offset -> gates -> beats -> sequence
 * 
 * Gates are encoded as a string like "1000" for gate1=true, gate3=false, gate4=false, gate5=false
 * Offset is boolean
 * Beats are 1, 2, 3, 4, etc.
 */

const COMPLETE_COURSE_SEQUENCES = {
  [COURSE_TYPES.T_LEEWARD]: {
    // Start finish together
    'Start finish together': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-F',
          2: 'S-1-4-1-F',
          3: 'S-1-4-1-4-1-F',
          4: 'S-1-4-1-4-1-4-1-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-F',
          2: 'S-1-4G-1-F',
          3: 'S-1-4G-1-4G-1-F',
          4: 'S-1-4G-1-4G-1-4G-1-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-F',
          2: 'S-1G-4-1G-F',
          3: 'S-1G-4-1G-4-1G-F',
          4: 'S-1G-4-1G-4-1G-4-1G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-F',
          2: 'S-1G-4G-1G-F',
          3: 'S-1G-4G-1G-4G-1G-F',
          4: 'S-1G-4G-1G-4G-1G-4G-1G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-F',
          2: 'S-1-1A-4-1-1A-F',
          3: 'S-1-1A-4-1-1A-4-1-1A-F',
          4: 'S-1-1A-4-1-1A-4-1-1A-4-1-1A-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-F',
          2: 'S-1-1A-4G-1-1A-F',
          3: 'S-1-1A-4G-1-1A-4G-1-1A-F',
          4: 'S-1-1A-4G-1-1A-4G-1-1A-4G-1-1A-F'
        },
        '1000': { // Gate 1 only - INVALID (offset and gate1 cannot coexist)
          valid: false
        },
        '1040': { // Gate 1 and Gate 4 - INVALID
          valid: false
        }
      }
    },
    
    // Leeward, Reach port
    'Leeward, Reach port': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-4P-F',
          2: 'S-1-4-1-4P-F',
          3: 'S-1-4-1-4-1-4P-F',
          4: 'S-1-4-1-4-1-4-1-4P-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-4GP-F',
          2: 'S-1-4G-1-4GP-F',
          3: 'S-1-4G-1-4G-1-4GP-F',
          4: 'S-1-4G-1-4G-1-4G-1-4GP-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4P-F',
          2: 'S-1G-4-1G-4P-F',
          3: 'S-1G-4-1G-4-1G-4P-F',
          4: 'S-1G-4-1G-4-1G-4-1G-4P-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4GP-F',
          2: 'S-1G-4G-1G-4GP-F',
          3: 'S-1G-4G-1G-4G-1G-4GP-F',
          4: 'S-1G-4G-1G-4G-1G-4G-1G-4GP-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-4P-F',
          2: 'S-1-1A-4-1-1A-4P-F',
          3: 'S-1-1A-4-1-1A-4-1-1A-4P-F',
          4: 'S-1-1A-4-1-1A-4-1-1A-4-1-1A-4P-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-4GP-F',
          2: 'S-1-1A-4G-1-1A-4GP-F',
          3: 'S-1-1A-4G-1-1A-4G-1-1A-4GP-F',
          4: 'S-1-1A-4G-1-1A-4G-1-1A-4G-1-1A-4GP-F'
        },
        '1000': { // Gate 1 only - INVALID
          valid: false
        },
        '1040': { // Gate 1 and Gate 4 - INVALID
          valid: false
        }
      }
    },
    
    // Leeward, Reach starboard
    'Leeward, Reach starboard': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-4S-F',
          2: 'S-1-4-1-4S-F',
          3: 'S-1-4-1-4-1-4S-F',
          4: 'S-1-4-1-4-1-4-1-4S-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-4GS-F',
          2: 'S-1-4G-1-4GS-F',
          3: 'S-1-4G-1-4G-1-4GS-F',
          4: 'S-1-4G-1-4G-1-4G-1-4GS-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4S-F',
          2: 'S-1G-4-1G-4S-F',
          3: 'S-1G-4-1G-4-1G-4S-F',
          4: 'S-1G-4-1G-4-1G-4-1G-4S-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4GS-F',
          2: 'S-1G-4G-1G-4GS-F',
          3: 'S-1G-4G-1G-4G-1G-4GS-F',
          4: 'S-1G-4G-1G-4G-1G-4G-1G-4GS-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-4S-F',
          2: 'S-1-1A-4-1-1A-4S-F',
          3: 'S-1-1A-4-1-1A-4-1-1A-4S-F',
          4: 'S-1-1A-4-1-1A-4-1-1A-4-1-1A-4S-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-4GS-F',
          2: 'S-1-1A-4G-1-1A-4GS-F',
          3: 'S-1-1A-4G-1-1A-4G-1-1A-4GS-F',
          4: 'S-1-1A-4G-1-1A-4G-1-1A-4G-1-1A-4GS-F'
        },
        '1000': { // Gate 1 only - INVALID
          valid: false
        },
        '1040': { // Gate 1 and Gate 4 - INVALID
          valid: false
        }
      }
    }
  },
  
  [COURSE_TYPES.T_WINDWARD]: {
    // Windward
    'Windward': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-F',
          2: 'S-1-4-F',
          3: 'S-1-4-1-4-F',
          4: 'S-1-4-1-4-1-4-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-F',
          2: 'S-1-4G-F',
          3: 'S-1-4G-1-4G-F',
          4: 'S-1-4G-1-4G-1-4G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-F',
          2: 'S-1G-4-F',
          3: 'S-1G-4-1G-4-F',
          4: 'S-1G-4-1G-4-1G-4-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-F',
          2: 'S-1G-4G-F',
          3: 'S-1G-4G-1G-4G-F',
          4: 'S-1G-4G-1G-4G-1G-4G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-F',
          2: 'S-1-1A-4-F',
          3: 'S-1-1A-4-1-1A-4-F',
          4: 'S-1-1A-4-1-1A-4-1-1A-4-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-F',
          2: 'S-1-1A-4G-F',
          3: 'S-1-1A-4G-1-1A-4G-F',
          4: 'S-1-1A-4G-1-1A-4G-1-1A-4G-F'
        },
        '1000': { // Gate 1 only - INVALID
          valid: false
        },
        '1040': { // Gate 1 and Gate 4 - INVALID
          valid: false
        }
      }
    },
    
    // Windward, Reach port
    'Windward, Reach port': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1P-F',
          2: 'S-1-4-1P-F',
          3: 'S-1-4-1-4-1P-F',
          4: 'S-1-4-1-4-1-4-1P-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1P-F',
          2: 'S-1-4G-1P-F',
          3: 'S-1-4G-1-4G-1P-F',
          4: 'S-1-4G-1-4G-1-4G-1P-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1GP-F',
          2: 'S-1G-4-1GP-F',
          3: 'S-1G-4-1G-4-1GP-F',
          4: 'S-1G-4-1G-4-1G-4-1GP-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1GP-F',
          2: 'S-1G-4G-1GP-F',
          3: 'S-1G-4G-1G-4G-1GP-F',
          4: 'S-1G-4G-1G-4G-1G-4G-1GP-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-1P-F',
          2: 'S-1-1A-4-1P-F',
          3: 'S-1-1A-4-1-1A-4-1P-F',
          4: 'S-1-1A-4-1-1A-4-1-1A-4-1P-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-1P-F',
          2: 'S-1-1A-4G-1P-F',
          3: 'S-1-1A-4G-1-1A-4G-1P-F',
          4: 'S-1-1A-4G-1-1A-4G-1-1A-4G-1P-F'
        },
        '1000': { // Gate 1 only - INVALID
          valid: false
        },
        '1040': { // Gate 1 and Gate 4 - INVALID
          valid: false
        }
      }
    },
    
    // Windward, Reach starboard
    'Windward, Reach starboard': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1S-F',
          2: 'S-1-4-1S-F',
          3: 'S-1-4-1-4-1S-F',
          4: 'S-1-4-1-4-1-4-1S-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1S-F',
          2: 'S-1-4G-1S-F',
          3: 'S-1-4G-1-4G-1S-F',
          4: 'S-1-4G-1-4G-1-4G-1S-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1GS-F',
          2: 'S-1G-4-1GS-F',
          3: 'S-1G-4-1G-4-1GS-F',
          4: 'S-1G-4-1G-4-1G-4-1GS-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1GS-F',
          2: 'S-1G-4G-1GS-F',
          3: 'S-1G-4G-1G-4G-1GS-F',
          4: 'S-1G-4G-1G-4G-1G-4G-1GS-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-1S-F',
          2: 'S-1-1A-4-1S-F',
          3: 'S-1-1A-4-1-1A-4-1S-F',
          4: 'S-1-1A-4-1-1A-4-1-1A-4-1S-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-1S-F',
          2: 'S-1-1A-4G-1S-F',
          3: 'S-1-1A-4G-1-1A-4G-1S-F',
          4: 'S-1-1A-4G-1-1A-4G-1-1A-4G-1S-F'
        },
        '1000': { // Gate 1 only - INVALID
          valid: false
        },
        '1040': { // Gate 1 and Gate 4 - INVALID
          valid: false
        }
      }
    }
  },
  
  [COURSE_TYPES.T_INNER_TRAPEZOID]: {
    // Start finish together, Reach = half beat length
    'Start finish together. Reach = half beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-4-1-2-3-F', // Fixed trapezoid pattern
          2: 'S-1-4-1-2-3-F', // Same for all beats
          3: 'S-1-4-1-2-3-F',
          4: 'S-1-4-1-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-4-1-2-3G-F',
          2: 'S-1-4-1-2-3G-F', 
          3: 'S-1-4-1-2-3G-F',
          4: 'S-1-4-1-2-3G-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-4G-1-2-3-F',
          2: 'S-1-4G-1-2-3-F',
          3: 'S-1-4G-1-2-3-F', 
          4: 'S-1-4G-1-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-4G-1-2-3G-F',
          2: 'S-1-4G-1-2-3G-F',
          3: 'S-1-4G-1-2-3G-F',
          4: 'S-1-4G-1-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3-F',
          2: 'S-1-1A-4-1-1A-2-3-F',
          3: 'S-1-1A-4-1-1A-2-3-F',
          4: 'S-1-1A-4-1-1A-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3G-F',
          2: 'S-1-1A-4-1-1A-2-3G-F',
          3: 'S-1-1A-4-1-1A-2-3G-F',
          4: 'S-1-1A-4-1-1A-2-3G-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3-F',
          2: 'S-1-1A-4G-1-1A-2-3-F',
          3: 'S-1-1A-4G-1-1A-2-3-F',
          4: 'S-1-1A-4G-1-1A-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3G-F',
          2: 'S-1-1A-4G-1-1A-2-3G-F',
          3: 'S-1-1A-4G-1-1A-2-3G-F',
          4: 'S-1-1A-4G-1-1A-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      }
    },
    
    // Separate Start finish, Reach = half beat length
    'Separate Start finish. Reach = half beat length': {
      false: { // No offset
        '0000': { // No gates  
          valid: true,
          1: 'S-1-4-1-2-3-F',
          2: 'S-1-4-1-2-3-F',
          3: 'S-1-4-1-2-3-F',
          4: 'S-1-4-1-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-4-1-2-3G-F',
          2: 'S-1-4-1-2-3G-F',
          3: 'S-1-4-1-2-3G-F',
          4: 'S-1-4-1-2-3G-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-4G-1-2-3-F',
          2: 'S-1-4G-1-2-3-F',
          3: 'S-1-4G-1-2-3-F',
          4: 'S-1-4G-1-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-4G-1-2-3G-F',
          2: 'S-1-4G-1-2-3G-F',
          3: 'S-1-4G-1-2-3G-F',
          4: 'S-1-4G-1-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3-F',
          2: 'S-1-1A-4-1-1A-2-3-F',
          3: 'S-1-1A-4-1-1A-2-3-F',
          4: 'S-1-1A-4-1-1A-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3G-F',
          2: 'S-1-1A-4-1-1A-2-3G-F',
          3: 'S-1-1A-4-1-1A-2-3G-F',
          4: 'S-1-1A-4-1-1A-2-3G-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3-F',
          2: 'S-1-1A-4G-1-1A-2-3-F',
          3: 'S-1-1A-4G-1-1A-2-3-F',
          4: 'S-1-1A-4G-1-1A-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3G-F',
          2: 'S-1-1A-4G-1-1A-2-3G-F',
          3: 'S-1-1A-4G-1-1A-2-3G-F',
          4: 'S-1-1A-4G-1-1A-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      }
    },
    
    // Equal beats, Reach = half beat length
    'Equal beats. Reach = half beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-4-1-2-3-F',
          2: 'S-1-4-1-2-3-F',
          3: 'S-1-4-1-2-3-F',
          4: 'S-1-4-1-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-4-1-2-3G-F',
          2: 'S-1-4-1-2-3G-F',
          3: 'S-1-4-1-2-3G-F',
          4: 'S-1-4-1-2-3G-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-4G-1-2-3-F',
          2: 'S-1-4G-1-2-3-F',
          3: 'S-1-4G-1-2-3-F',
          4: 'S-1-4G-1-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-4G-1-2-3G-F',
          2: 'S-1-4G-1-2-3G-F',
          3: 'S-1-4G-1-2-3G-F',
          4: 'S-1-4G-1-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3-F',
          2: 'S-1-1A-4-1-1A-2-3-F',
          3: 'S-1-1A-4-1-1A-2-3-F',
          4: 'S-1-1A-4-1-1A-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3G-F',
          2: 'S-1-1A-4-1-1A-2-3G-F',
          3: 'S-1-1A-4-1-1A-2-3G-F',
          4: 'S-1-1A-4-1-1A-2-3G-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3-F',
          2: 'S-1-1A-4G-1-1A-2-3-F',
          3: 'S-1-1A-4G-1-1A-2-3-F',
          4: 'S-1-1A-4G-1-1A-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3G-F',
          2: 'S-1-1A-4G-1-1A-2-3G-F',
          3: 'S-1-1A-4G-1-1A-2-3G-F',
          4: 'S-1-1A-4G-1-1A-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      }
    },
    
    // Reach = 2/3 beat length
    'Reach = 2/3 beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-4-1-2-3-F',
          2: 'S-1-4-1-2-3-F',
          3: 'S-1-4-1-2-3-F',
          4: 'S-1-4-1-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-4-1-2-3G-F',
          2: 'S-1-4-1-2-3G-F',
          3: 'S-1-4-1-2-3G-F',
          4: 'S-1-4-1-2-3G-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-4G-1-2-3-F',
          2: 'S-1-4G-1-2-3-F',
          3: 'S-1-4G-1-2-3-F',
          4: 'S-1-4G-1-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-4G-1-2-3G-F',
          2: 'S-1-4G-1-2-3G-F',
          3: 'S-1-4G-1-2-3G-F',
          4: 'S-1-4G-1-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3-F',
          2: 'S-1-1A-4-1-1A-2-3-F',
          3: 'S-1-1A-4-1-1A-2-3-F',
          4: 'S-1-1A-4-1-1A-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3G-F',
          2: 'S-1-1A-4-1-1A-2-3G-F',
          3: 'S-1-1A-4-1-1A-2-3G-F',
          4: 'S-1-1A-4-1-1A-2-3G-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3-F',
          2: 'S-1-1A-4G-1-1A-2-3-F',
          3: 'S-1-1A-4G-1-1A-2-3-F',
          4: 'S-1-1A-4G-1-1A-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3G-F',
          2: 'S-1-1A-4G-1-1A-2-3G-F',
          3: 'S-1-1A-4G-1-1A-2-3G-F',
          4: 'S-1-1A-4G-1-1A-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      }
    },
    
    // Windward, Equal beats, Reach = 2/3 beat length
    'Windward, Equal beats, Reach = 2/3 beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-4-1-2-3-5-F',
          2: 'S-1-4-1-2-3-5-F',
          3: 'S-1-4-1-2-3-5-F',
          4: 'S-1-4-1-2-3-5-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-4-1-2-3G-5-F',
          2: 'S-1-4-1-2-3G-5-F',
          3: 'S-1-4-1-2-3G-5-F',
          4: 'S-1-4-1-2-3G-5-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-4G-1-2-3-5-F',
          2: 'S-1-4G-1-2-3-5-F',
          3: 'S-1-4G-1-2-3-5-F',
          4: 'S-1-4G-1-2-3-5-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-4G-1-2-3G-5-F',
          2: 'S-1-4G-1-2-3G-5-F',
          3: 'S-1-4G-1-2-3G-5-F',
          4: 'S-1-4G-1-2-3G-5-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3-5-F',
          2: 'S-1-1A-4-1-1A-2-3-5-F',
          3: 'S-1-1A-4-1-1A-2-3-5-F',
          4: 'S-1-1A-4-1-1A-2-3-5-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3G-5-F',
          2: 'S-1-1A-4-1-1A-2-3G-5-F',
          3: 'S-1-1A-4-1-1A-2-3G-5-F',
          4: 'S-1-1A-4-1-1A-2-3G-5-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3-5-F',
          2: 'S-1-1A-4G-1-1A-2-3-5-F',
          3: 'S-1-1A-4G-1-1A-2-3-5-F',
          4: 'S-1-1A-4G-1-1A-2-3-5-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3G-5-F',
          2: 'S-1-1A-4G-1-1A-2-3G-5-F',
          3: 'S-1-1A-4G-1-1A-2-3G-5-F',
          4: 'S-1-1A-4G-1-1A-2-3G-5-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      }
    },
    
    // Windward, Equal beats, Reach = 2/3 beat, Start finish together
    'Windward, Equal beats, Reach = 2/3 beat, Start finish together': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-4-1-2-3-5-F',
          2: 'S-1-4-1-2-3-5-F',
          3: 'S-1-4-1-2-3-5-F',
          4: 'S-1-4-1-2-3-5-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-4-1-2-3G-5-F',
          2: 'S-1-4-1-2-3G-5-F',
          3: 'S-1-4-1-2-3G-5-F',
          4: 'S-1-4-1-2-3G-5-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-4G-1-2-3-5-F',
          2: 'S-1-4G-1-2-3-5-F',
          3: 'S-1-4G-1-2-3-5-F',
          4: 'S-1-4G-1-2-3-5-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-4G-1-2-3G-5-F',
          2: 'S-1-4G-1-2-3G-5-F',
          3: 'S-1-4G-1-2-3G-5-F',
          4: 'S-1-4G-1-2-3G-5-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3-5-F',
          2: 'S-1-1A-4-1-1A-2-3-5-F',
          3: 'S-1-1A-4-1-1A-2-3-5-F',
          4: 'S-1-1A-4-1-1A-2-3-5-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-4-1-1A-2-3G-5-F',
          2: 'S-1-1A-4-1-1A-2-3G-5-F',
          3: 'S-1-1A-4-1-1A-2-3G-5-F',
          4: 'S-1-1A-4-1-1A-2-3G-5-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3-5-F',
          2: 'S-1-1A-4G-1-1A-2-3-5-F',
          3: 'S-1-1A-4G-1-1A-2-3-5-F',
          4: 'S-1-1A-4G-1-1A-2-3-5-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-4G-1-1A-2-3G-5-F',
          2: 'S-1-1A-4G-1-1A-2-3G-5-F',
          3: 'S-1-1A-4G-1-1A-2-3G-5-F',
          4: 'S-1-1A-4G-1-1A-2-3G-5-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-4-1G-2-3-F',
          2: 'S-1G-4-1G-2-3-F',
          3: 'S-1G-4-1G-2-3-F',
          4: 'S-1G-4-1G-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-4-1G-2-3G-F',
          2: 'S-1G-4-1G-2-3G-F',
          3: 'S-1G-4-1G-2-3G-F',
          4: 'S-1G-4-1G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3-F',
          2: 'S-1G-4G-1G-2-3-F',
          3: 'S-1G-4G-1G-2-3-F',
          4: 'S-1G-4G-1G-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-4G-1G-2-3G-F',
          2: 'S-1G-4G-1G-2-3G-F',
          3: 'S-1G-4G-1G-2-3G-F',
          4: 'S-1G-4G-1G-2-3G-F'
        }
      }
    }
  },
  
  [COURSE_TYPES.T_OUTER_TRAPEZOID]: {
    // Start finish together, Reach = half beat length
    'Start finish together. Reach = half beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-2-3-2-3-F',
          2: 'S-1-2-3-2-3-F',
          3: 'S-1-2-3-2-3-F',
          4: 'S-1-2-3-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-2-3G-2-3G-F',
          2: 'S-1-2-3G-2-3G-F',
          3: 'S-1-2-3G-2-3G-F',
          4: 'S-1-2-3G-2-3G-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-2-3-2-3-F',
          2: 'S-1-2-3-2-3-F',
          3: 'S-1-2-3-2-3-F',
          4: 'S-1-2-3-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-2-3G-2-3G-F',
          2: 'S-1-2-3G-2-3G-F',
          3: 'S-1-2-3G-2-3G-F',
          4: 'S-1-2-3G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-2-3-2-3-F',
          2: 'S-1-1A-2-3-2-3-F',
          3: 'S-1-1A-2-3-2-3-F',
          4: 'S-1-1A-2-3-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-F',
          2: 'S-1-1A-2-3G-2-3G-F',
          3: 'S-1-1A-2-3G-2-3G-F',
          4: 'S-1-1A-2-3G-2-3G-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-1A-2-3-2-3-F',
          2: 'S-1-1A-2-3-2-3-F',
          3: 'S-1-1A-2-3-2-3-F',
          4: 'S-1-1A-2-3-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-F',
          2: 'S-1-1A-2-3G-2-3G-F',
          3: 'S-1-1A-2-3G-2-3G-F',
          4: 'S-1-1A-2-3G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      }
    },
    
    // Separate Start finish, Reach = half beat length
    'Separate Start finish. Reach = half beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-2-3-2-3-F',
          2: 'S-1-2-3-2-3-F',
          3: 'S-1-2-3-2-3-F',
          4: 'S-1-2-3-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-2-3G-2-3G-F',
          2: 'S-1-2-3G-2-3G-F',
          3: 'S-1-2-3G-2-3G-F',
          4: 'S-1-2-3G-2-3G-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-2-3-2-3-F',
          2: 'S-1-2-3-2-3-F',
          3: 'S-1-2-3-2-3-F',
          4: 'S-1-2-3-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-2-3G-2-3G-F',
          2: 'S-1-2-3G-2-3G-F',
          3: 'S-1-2-3G-2-3G-F',
          4: 'S-1-2-3G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-2-3-2-3-F',
          2: 'S-1-1A-2-3-2-3-F',
          3: 'S-1-1A-2-3-2-3-F',
          4: 'S-1-1A-2-3-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-F',
          2: 'S-1-1A-2-3G-2-3G-F',
          3: 'S-1-1A-2-3G-2-3G-F',
          4: 'S-1-1A-2-3G-2-3G-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-1A-2-3-2-3-F',
          2: 'S-1-1A-2-3-2-3-F',
          3: 'S-1-1A-2-3-2-3-F',
          4: 'S-1-1A-2-3-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-F',
          2: 'S-1-1A-2-3G-2-3G-F',
          3: 'S-1-1A-2-3G-2-3G-F',
          4: 'S-1-1A-2-3G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      }
    },
    
    // Equal beats, Reach = half beat length
    'Equal beats. Reach = half beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-2-3-2-3-F',
          2: 'S-1-2-3-2-3-F',
          3: 'S-1-2-3-2-3-F',
          4: 'S-1-2-3-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-2-3G-2-3G-F',
          2: 'S-1-2-3G-2-3G-F',
          3: 'S-1-2-3G-2-3G-F',
          4: 'S-1-2-3G-2-3G-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-2-3-2-3-F',
          2: 'S-1-2-3-2-3-F',
          3: 'S-1-2-3-2-3-F',
          4: 'S-1-2-3-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-2-3G-2-3G-F',
          2: 'S-1-2-3G-2-3G-F',
          3: 'S-1-2-3G-2-3G-F',
          4: 'S-1-2-3G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-2-3-2-3-F',
          2: 'S-1-1A-2-3-2-3-F',
          3: 'S-1-1A-2-3-2-3-F',
          4: 'S-1-1A-2-3-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-F',
          2: 'S-1-1A-2-3G-2-3G-F',
          3: 'S-1-1A-2-3G-2-3G-F',
          4: 'S-1-1A-2-3G-2-3G-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-1A-2-3-2-3-F',
          2: 'S-1-1A-2-3-2-3-F',
          3: 'S-1-1A-2-3-2-3-F',
          4: 'S-1-1A-2-3-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-F',
          2: 'S-1-1A-2-3G-2-3G-F',
          3: 'S-1-1A-2-3G-2-3G-F',
          4: 'S-1-1A-2-3G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      }
    },
    
    // Reach = 2/3 beat length
    'Reach = 2/3 beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-2-3-2-3-F',
          2: 'S-1-2-3-2-3-F',
          3: 'S-1-2-3-2-3-F',
          4: 'S-1-2-3-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-2-3G-2-3G-F',
          2: 'S-1-2-3G-2-3G-F',
          3: 'S-1-2-3G-2-3G-F',
          4: 'S-1-2-3G-2-3G-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-2-3-2-3-F',
          2: 'S-1-2-3-2-3-F',
          3: 'S-1-2-3-2-3-F',
          4: 'S-1-2-3-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-2-3G-2-3G-F',
          2: 'S-1-2-3G-2-3G-F',
          3: 'S-1-2-3G-2-3G-F',
          4: 'S-1-2-3G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-2-3-2-3-F',
          2: 'S-1-1A-2-3-2-3-F',
          3: 'S-1-1A-2-3-2-3-F',
          4: 'S-1-1A-2-3-2-3-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-F',
          2: 'S-1-1A-2-3G-2-3G-F',
          3: 'S-1-1A-2-3G-2-3G-F',
          4: 'S-1-1A-2-3G-2-3G-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-1A-2-3-2-3-F',
          2: 'S-1-1A-2-3-2-3-F',
          3: 'S-1-1A-2-3-2-3-F',
          4: 'S-1-1A-2-3-2-3-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-F',
          2: 'S-1-1A-2-3G-2-3G-F',
          3: 'S-1-1A-2-3G-2-3G-F',
          4: 'S-1-1A-2-3G-2-3G-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      }
    },
    
    // Windward, Equal beats, Reach = 2/3 beat length
    'Windward, Equal beats, Reach = 2/3 beat length': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-2-3-2-3-5-F',
          2: 'S-1-2-3-2-3-5-F',
          3: 'S-1-2-3-2-3-5-F',
          4: 'S-1-2-3-2-3-5-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-2-3G-2-3G-5-F',
          2: 'S-1-2-3G-2-3G-5-F',
          3: 'S-1-2-3G-2-3G-5-F',
          4: 'S-1-2-3G-2-3G-5-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-2-3-2-3-5-F',
          2: 'S-1-2-3-2-3-5-F',
          3: 'S-1-2-3-2-3-5-F',
          4: 'S-1-2-3-2-3-5-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-2-3G-2-3G-5-F',
          2: 'S-1-2-3G-2-3G-5-F',
          3: 'S-1-2-3G-2-3G-5-F',
          4: 'S-1-2-3G-2-3G-5-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-2-3-2-3-5-F',
          2: 'S-1-1A-2-3-2-3-5-F',
          3: 'S-1-1A-2-3-2-3-5-F',
          4: 'S-1-1A-2-3-2-3-5-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-5-F',
          2: 'S-1-1A-2-3G-2-3G-5-F',
          3: 'S-1-1A-2-3G-2-3G-5-F',
          4: 'S-1-1A-2-3G-2-3G-5-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-1A-2-3-2-3-5-F',
          2: 'S-1-1A-2-3-2-3-5-F',
          3: 'S-1-1A-2-3-2-3-5-F',
          4: 'S-1-1A-2-3-2-3-5-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-5-F',
          2: 'S-1-1A-2-3G-2-3G-5-F',
          3: 'S-1-1A-2-3G-2-3G-5-F',
          4: 'S-1-1A-2-3G-2-3G-5-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      }
    },
    
    // Windward, Equal beats, Reach = 2/3 beat, Start finish together
    'Windward, Equal beats, Reach = 2/3 beat, Start finish together': {
      false: { // No offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-2-3-2-3-5-F',
          2: 'S-1-2-3-2-3-5-F',
          3: 'S-1-2-3-2-3-5-F',
          4: 'S-1-2-3-2-3-5-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-2-3G-2-3G-5-F',
          2: 'S-1-2-3G-2-3G-5-F',
          3: 'S-1-2-3G-2-3G-5-F',
          4: 'S-1-2-3G-2-3G-5-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-2-3-2-3-5-F',
          2: 'S-1-2-3-2-3-5-F',
          3: 'S-1-2-3-2-3-5-F',
          4: 'S-1-2-3-2-3-5-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-2-3G-2-3G-5-F',
          2: 'S-1-2-3G-2-3G-5-F',
          3: 'S-1-2-3G-2-3G-5-F',
          4: 'S-1-2-3G-2-3G-5-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      },
      true: { // With offset
        '0000': { // No gates
          valid: true,
          1: 'S-1-1A-2-3-2-3-5-F',
          2: 'S-1-1A-2-3-2-3-5-F',
          3: 'S-1-1A-2-3-2-3-5-F',
          4: 'S-1-1A-2-3-2-3-5-F'
        },
        '0300': { // Gate 3 only
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-5-F',
          2: 'S-1-1A-2-3G-2-3G-5-F',
          3: 'S-1-1A-2-3G-2-3G-5-F',
          4: 'S-1-1A-2-3G-2-3G-5-F'
        },
        '0040': { // Gate 4 only (same as no gates for outer trapezoid)
          valid: true,
          1: 'S-1-1A-2-3-2-3-5-F',
          2: 'S-1-1A-2-3-2-3-5-F',
          3: 'S-1-1A-2-3-2-3-5-F',
          4: 'S-1-1A-2-3-2-3-5-F'
        },
        '0340': { // Gate 3 and Gate 4
          valid: true,
          1: 'S-1-1A-2-3G-2-3G-5-F',
          2: 'S-1-1A-2-3G-2-3G-5-F',
          3: 'S-1-1A-2-3G-2-3G-5-F',
          4: 'S-1-1A-2-3G-2-3G-5-F'
        },
        '1000': { // Gate 1 only
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1300': { // Gate 1 and Gate 3
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        },
        '1040': { // Gate 1 and Gate 4
          valid: true,
          1: 'S-1G-2-3-2-3-F',
          2: 'S-1G-2-3-2-3-F',
          3: 'S-1G-2-3-2-3-F',
          4: 'S-1G-2-3-2-3-F'
        },
        '1340': { // Gate 1, Gate 3 and Gate 4
          valid: true,
          1: 'S-1G-2-3G-2-3G-F',
          2: 'S-1G-2-3G-2-3G-F',
          3: 'S-1G-2-3G-2-3G-F',
          4: 'S-1G-2-3G-2-3G-F'
        }
      }
    }
  }
  ,
  [COURSE_TYPES.T_TRIANGLE]: {
    'Start finish together': {
      false: { // No offset (triangle doesn't support offset)
        '0000': { // No gates
          valid: true,
          1: 'S-1-F',
          2: 'S-1-2-4-1-F',
          3: 'S-1-2-4-1-2-4-1-F',
          4: 'S-1-2-4-1-2-4-1-2-4-1-F'
        },
        '0040': { // Gate 4 only
          valid: true,
          1: 'S-1-F',
          2: 'S-1-2-4G-1-F',
          3: 'S-1-2-4G-1-2-4G-1-F',
          4: 'S-1-2-4G-1-2-4G-1-2-4G-1-F'
        }
      }
    }
  }
};

/**
 * Generate a gate configuration key from gate settings
 * Uses actual gate numbers for readability: 1000 = gate1, 0040 = gate4, 1040 = gate1+gate4
 */
function generateGateKey(gates = {}) {
  const gate1 = gates.gate1 ? '1' : '0';
  const gate3 = gates.gate3 ? '3' : '0';
  const gate4 = gates.gate4 ? '4' : '0';
  const gate5 = gates.gate5 ? '5' : '0';
  return gate1 + gate3 + gate4 + gate5;
}

/**
 * Get course sequence using complete lookup table
 * @param {string} courseType - Course type
 * @param {string} finishType - Finish type
 * @param {object} gates - Gate configuration
 * @param {boolean} offset - Whether offset is enabled
 * @param {number} beats - Number of windward beats
 * @returns {string} Course sequence or null if not found/invalid
 */
export function generateSequenceFromCompleteLookup(courseType, finishType, gates = {}, offset = false, beats = 2) {
  const courseConfig = COMPLETE_COURSE_SEQUENCES[courseType];
  if (!courseConfig) {
    return null;
  }
  
  const finishConfig = courseConfig[finishType];
  if (!finishConfig) {
    return null;
  }
  
  const offsetConfig = finishConfig[offset];
  if (!offsetConfig) {
    return null;
  }
  
  if (gates.gate1 && offset) {
    console.warn('⚠️ Warning: Gate 1 cannot be used with offset.');
    return null;
  }

  const gateKey = generateGateKey(gates);
  console.log('🔍 Complete lookup:', courseType, finishType, 'offset:', offset, 'gateKey:', gateKey, 'beats:', beats);
  
  const gateConfig = offsetConfig[gateKey];
  if (!gateConfig) {
    console.log('❌ No gate config found for:', gateKey);
    return null;
  }
  
  // Check if this configuration is valid
  if (!gateConfig.valid) {
    return null; // Invalid configuration
  }
  
  // Get sequence for the specified number of beats
  return gateConfig[beats] || null;
}

/**
 * Check if a course configuration is valid
 * @param {string} courseType - Course type
 * @param {string} finishType - Finish type
 * @param {object} gates - Gate configuration
 * @param {boolean} offset - Whether offset is enabled
 * @returns {boolean} True if configuration is valid
 */
export function isValidCourseConfiguration(courseType, finishType, gates = {}, offset = false) {
  const courseConfig = COMPLETE_COURSE_SEQUENCES[courseType];
  if (!courseConfig) return false;
  
  const finishConfig = courseConfig[finishType];
  if (!finishConfig) return false;
  
  const offsetConfig = finishConfig[offset];
  if (!offsetConfig) return false;
  
  const gateKey = generateGateKey(gates);
  const gateConfig = offsetConfig[gateKey];
  if (!gateConfig) return false;
  
  return gateConfig.valid === true;
}

/**
 * Get available finish types for a course type
 * @param {string} courseType - Course type
 * @returns {string[]} - Array of available finish types
 */
function getAvailableFinishTypes(courseType) {
  const courseConfig = COMPLETE_COURSE_SEQUENCES[courseType];
  if (!courseConfig) return [];
  
  return Object.keys(courseConfig);
}

/**
 * Get available gate combinations for a course type and finish type
 * @param {string} courseType - Course type
 * @param {string} finishType - Finish type
 * @param {boolean} offset - Whether offset is enabled
 * @returns {object[]} - Array of valid gate combinations
 */
function getAvailableGateCombinations(courseType, finishType, offset) {
  const finishConfig = COMPLETE_COURSE_SEQUENCES[courseType]?.[finishType];
  if (!finishConfig) return [];
  
  const offsetConfig = finishConfig[offset];
  if (!offsetConfig) return [];
  
  const validCombinations = [];
  
  Object.keys(offsetConfig).forEach(gateKey => {
    if (offsetConfig[gateKey].valid) {
      // Convert gate key back to gate object
      const gates = {
        gate1: gateKey[0] === '1',
        gate3: gateKey[1] === '3',
        gate4: gateKey[2] === '4',
        gate5: gateKey[3] === '5'
      };
      validCombinations.push({ gateKey, gates });
    }
  });
  
  return validCombinations;
}

/**
 * Validate and adjust course configuration when switching course types
 * Strict rules: 1) non-exact finish type → first available, 2) offset not allowed → remove offset, 3) gate not listed → remove gate
 * @param {object} config - Configuration with courseType and other properties
 * @returns {object} - { isValid, adjustedConfig, adjustments }
 */
function validateAndAdjustConfig(config) {
  const adjustedConfig = { ...config };
  const adjustments = [];
  
  // RULE 1: If finish type isn't an exact match, use "first" finish type for new course type
  const availableFinishTypes = getAvailableFinishTypes(config.courseType);
  if (!availableFinishTypes.includes(config.finishType)) {
    const newFinishType = availableFinishTypes[0] || 'Start finish together';
    adjustedConfig.finishType = newFinishType;
    adjustments.push(`Finish type changed to "${newFinishType}"`);
  }
  
  // RULE 2: If offset isn't allowed, remove offset
  const availableWithOffset = getAvailableGateCombinations(config.courseType, adjustedConfig.finishType, true);
  const availableWithoutOffset = getAvailableGateCombinations(config.courseType, adjustedConfig.finishType, false);
  
  if (config.offsetEnabled && availableWithOffset.length === 0) {
    // Offset not supported at all for this course/finish type
    adjustedConfig.offsetEnabled = false;
    adjustments.push('Offset removed (not supported)');
  }
  
  // RULE 3: If gate isn't listed, remove gate
  const requestedGates = config.gates || [];
  const availableGatesForCurrentOffset = adjustedConfig.offsetEnabled ? availableWithOffset : availableWithoutOffset;
  
  // Get all supported gate combinations for this configuration
  const supportedGateKeys = availableGatesForCurrentOffset.map(combo => combo.gateKey);
  
  // Convert current gates to gate config
  const currentGateConfig = {
    gate1: requestedGates.includes('1') || requestedGates.includes(1),
    gate3: requestedGates.includes('3') || requestedGates.includes(3),
    gate4: requestedGates.includes('4') || requestedGates.includes(4),
    gate5: requestedGates.includes('5') || requestedGates.includes(5)
  };
  
  const currentGateKey = generateGateKey(currentGateConfig);
  
  // Check if current gate combination is supported
  if (!supportedGateKeys.includes(currentGateKey)) {
    // Remove gates one by one until we find a valid combination, or end up with no gates
    const validGates = [];
    
    // Check each gate individually - keep only the ones that work
    for (const gate of requestedGates) {
      const testGateConfig = {
        gate1: gate === '1' || gate === 1,
        gate3: gate === '3' || gate === 3,
        gate4: gate === '4' || gate === 4,
        gate5: gate === '5' || gate === 5
      };
      
      const testGateKey = generateGateKey(testGateConfig);
      if (supportedGateKeys.includes(testGateKey)) {
        validGates.push(gate);
      }
    }
    
    adjustedConfig.gates = validGates;
    if (validGates.length !== requestedGates.length) {
      const removedGates = requestedGates.filter(gate => !validGates.includes(gate));
      adjustments.push(`Removed unsupported gates: ${removedGates.join(', ')}`);
    }
    
    // Final check: if no individual gates work, try no gates
    if (validGates.length === 0) {
      const noGatesConfig = { gate1: false, gate3: false, gate4: false, gate5: false };
      const noGatesKey = generateGateKey(noGatesConfig);
      if (supportedGateKeys.includes(noGatesKey)) {
        adjustedConfig.gates = [];
        adjustments.push('All gates removed (no gates supported)');
      }
    }
  }
  
  return {
    isValid: adjustments.length === 0,
    adjustedConfig,
    adjustments
  };
}

export { 
  COMPLETE_COURSE_SEQUENCES,
  getAvailableFinishTypes,
  getAvailableGateCombinations, 
  validateAndAdjustConfig
};